// Standard IEEE 802.3 CRC-32 table computation
const makeCrcTable = (): Uint32Array => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let k = 0; k < 8; k++) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[i] = c >>> 0;
  }
  return table;
};

const crcTable = makeCrcTable();

export const calculateCrc32 = (data: Uint8Array): number => {
  let crc = 0xffffffff;
  for (let i = 0; i < data.length; i++) {
    crc = (crc >>> 8) ^ crcTable[(crc ^ data[i]) & 0xff];
  }
  return (crc ^ 0xffffffff) >>> 0;
};

export interface ZipFileInput {
  name: string;
  data: Uint8Array;
}

/**
 * Converts a base64 DataURL (e.g. data:image/png;base64,...) to a Uint8Array byte buffer.
 */
export const dataUrlToUint8Array = (dataUrl: string): Uint8Array => {
  const base64Index = dataUrl.indexOf(';base64,');
  const base64 = base64Index >= 0 ? dataUrl.slice(base64Index + 8) : dataUrl;
  const binaryStr = atob(base64);
  const len = binaryStr.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryStr.charCodeAt(i);
  }
  return bytes;
};

/**
 * Creates a valid PKZIP .zip Blob containing the supplied files using standard Store (uncompressed) method.
 * Since PNG and JPEG image files are already compressed streams, Stored ZIP format creates
 * universally compatible archives with zero decompression loss across macOS, Windows, Linux, iOS & Android.
 */
export const createZipBlob = (files: ZipFileInput[]): Blob => {
  const localHeaders: Uint8Array[] = [];
  const centralHeaders: Uint8Array[] = [];

  let currentOffset = 0;
  const now = new Date();

  // DOS Date and Time representation
  const dosTime =
    ((now.getHours() & 0x1f) << 11) |
    ((now.getMinutes() & 0x3f) << 5) |
    ((Math.floor(now.getSeconds() / 2)) & 0x1f);

  const dosDate =
    (((now.getFullYear() - 1980) & 0x7f) << 9) |
    (((now.getMonth() + 1) & 0x0f) << 5) |
    (now.getDate() & 0x1f);

  const encoder = new TextEncoder();

  for (const file of files) {
    const fileNameBytes = encoder.encode(file.name);
    const fileData = file.data;
    const crc = calculateCrc32(fileData);
    const size = fileData.length;

    // --- Local File Header (30 bytes + filename) ---
    const localHeader = new Uint8Array(30 + fileNameBytes.length);
    const lv = new DataView(localHeader.buffer);

    lv.setUint32(0, 0x04034b50, true); // Local file header signature
    lv.setUint16(4, 20, true); // Version needed to extract (2.0)
    lv.setUint16(6, 0x0800, true); // General purpose bit flag (UTF-8 filename flag)
    lv.setUint16(8, 0, true); // Compression method (0 = Store)
    lv.setUint16(10, dosTime, true);
    lv.setUint16(12, dosDate, true);
    lv.setUint32(14, crc, true); // CRC-32
    lv.setUint32(18, size, true); // Compressed size
    lv.setUint32(22, size, true); // Uncompressed size
    lv.setUint16(26, fileNameBytes.length, true); // Filename length
    lv.setUint16(28, 0, true); // Extra field length

    localHeader.set(fileNameBytes, 30);

    localHeaders.push(localHeader);
    localHeaders.push(fileData);

    // --- Central Directory File Header (46 bytes + filename) ---
    const centralHeader = new Uint8Array(46 + fileNameBytes.length);
    const cv = new DataView(centralHeader.buffer);

    cv.setUint32(0, 0x02014b50, true); // Central directory file header signature
    cv.setUint16(4, 20, true); // Version made by
    cv.setUint16(6, 20, true); // Version needed to extract
    cv.setUint16(8, 0x0800, true); // General purpose bit flag (UTF-8)
    cv.setUint16(10, 0, true); // Compression method (0 = Store)
    cv.setUint16(12, dosTime, true);
    cv.setUint16(14, dosDate, true);
    cv.setUint32(16, crc, true); // CRC-32
    cv.setUint32(20, size, true); // Compressed size
    cv.setUint32(24, size, true); // Uncompressed size
    cv.setUint16(28, fileNameBytes.length, true); // Filename length
    cv.setUint16(30, 0, true); // Extra field length
    cv.setUint16(32, 0, true); // File comment length
    cv.setUint16(34, 0, true); // Disk number start
    cv.setUint16(36, 0, true); // Internal file attributes
    cv.setUint32(38, 0, true); // External file attributes
    cv.setUint32(42, currentOffset, true); // Relative offset of local header

    centralHeader.set(fileNameBytes, 46);
    centralHeaders.push(centralHeader);

    currentOffset += localHeader.length + fileData.length;
  }

  const centralDirOffset = currentOffset;
  let centralDirSize = 0;
  for (const ch of centralHeaders) {
    centralDirSize += ch.length;
  }

  // --- End of Central Directory Record (22 bytes) ---
  const eocd = new Uint8Array(22);
  const ev = new DataView(eocd.buffer);

  ev.setUint32(0, 0x06054b50, true); // End of central dir signature
  ev.setUint16(4, 0, true); // Number of this disk
  ev.setUint16(6, 0, true); // Disk where central directory starts
  ev.setUint16(8, files.length, true); // Number of central directory records on this disk
  ev.setUint16(10, files.length, true); // Total number of central directory records
  ev.setUint32(12, centralDirSize, true); // Size of central directory
  ev.setUint32(16, centralDirOffset, true); // Offset of start of central directory
  ev.setUint16(20, 0, true); // Comment length

  return new Blob([...localHeaders, ...centralHeaders, eocd], {
    type: 'application/zip',
  });
};
